import React, { useState } from 'react';
import { Card, Progress, Tag, Typography, Row, Col, Space, Divider, Collapse } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface FeatureItem {
  id: string;
  title: string;
  status: 'done' | 'in-progress' | 'pending';
  description?: string;
}

interface FeatureCategory {
  id: string;
  title: string;
  phase: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  features: FeatureItem[];
}

const ProgressPage: React.FC = () => {
  const [features] = useState<FeatureCategory[]>([
    {
      id: 'phase1-user',
      title: 'User Management Enhancements',
      phase: 'Phase 1: Foundation',
      priority: 'Critical',
      features: [
        { id: 'user-search', title: 'User search by username, mobile, email', status: 'done' },
        { id: 'user-detail', title: 'User profile detail page with complete info', status: 'done' },
        { id: 'user-suspend', title: 'Account suspend/ban/unblock', status: 'done' },
        { id: 'user-restrict', title: 'Posting permission restrict & marketplace disable', status: 'done' },
        { id: 'user-bulk-delete', title: 'Bulk delete/disable all user posts', status: 'done' },
      ],
    },
    {
      id: 'phase1-post',
      title: 'Post/Product Management',
      phase: 'Phase 1: Foundation',
      priority: 'Critical',
      features: [
        { id: 'post-fetch', title: 'Fetch post by ID or direct link', status: 'done' },
        { id: 'post-delete', title: 'Delete/disable/hide posts', status: 'done' },
        { id: 'post-suspend', title: 'Temporary suspend posts', status: 'done' },
        { id: 'post-edit', title: 'Edit post (caption, tags, location, status)', status: 'done' },
        { id: 'post-reject', title: 'Reject post with reason + notification', status: 'done' },
      ],
    },
    {
      id: 'phase1-roles',
      title: 'Admin Roles & Permissions',
      phase: 'Phase 1: Foundation',
      priority: 'Critical',
      features: [
        { id: 'roles-system', title: 'Create role system (Super Admin, Moderator, Support)', status: 'done' },
        { id: 'roles-permissions', title: 'Role-based permissions', status: 'done' },
        { id: 'roles-audit', title: 'Admin action logging', status: 'done' },
      ],
    },
    {
      id: 'phase2-moderation',
      title: 'Content Moderation',
      phase: 'Phase 2: Content & Moderation',
      priority: 'High',
      features: [
        { id: 'keywords-manage', title: 'Blocked keywords management', status: 'done' },
        { id: 'keywords-block', title: 'Keyword blocking on post creation', status: 'done' },
        { id: 'keywords-category', title: 'Category-wise keyword blocking', status: 'done' },
        { id: 'keywords-error', title: 'Customizable error messages', status: 'done' },
      ],
    },
    {
      id: 'phase2-reports',
      title: 'Reports & Abuse Handling',
      phase: 'Phase 2: Content & Moderation',
      priority: 'High',
      features: [
        { id: 'reports-dashboard', title: 'Reports dashboard', status: 'done' },
        { id: 'reports-types', title: 'Report types (Fake, Abuse, Scam, Cruelty)', status: 'done' },
        { id: 'reports-actions', title: 'Report actions (delete, warn, ban)', status: 'done' },
        { id: 'reports-tracking', title: 'Report status tracking', status: 'done' },
      ],
    },
    {
      id: 'phase2-badges',
      title: 'Badge & Verification System',
      phase: 'Phase 2: Content & Moderation',
      priority: 'High',
      features: [
        { id: 'badges-types', title: 'Badge types (Verified Seller, Trusted Breeder, NGO, Business)', status: 'done' },
        { id: 'badges-assign', title: 'Badge assignment UI', status: 'done' },
        { id: 'badges-expiry', title: 'Badge expiry management', status: 'done' },
      ],
    },
    {
      id: 'phase3-notifications',
      title: 'Notification System',
      phase: 'Phase 3: Communication & CMS',
      priority: 'High',
      features: [
        { id: 'notification-1', title: 'Individual user notification', status: 'done' },
        { id: 'notification-2', title: 'Bulk notification (selected users / all users)', status: 'done' },
        { id: 'notification-3', title: 'Alert types (Warning, Policy violation, Feature update, Account notice)', status: 'done' },
        { id: 'notification-4', title: 'Custom message editor', status: 'done' },
      ],
    },
    {
      id: 'phase3-cms',
      title: 'CMS (Content Management System)',
      phase: 'Phase 3: Communication & CMS',
      priority: 'Medium',
      features: [
        { id: 'cms-pages', title: 'Pages management (Help, Support, Contact, About)', status: 'done' },
        { id: 'cms-editor', title: 'HTML editor integration', status: 'done' },
        { id: 'cms-terms', title: 'Terms & Privacy Policy editor', status: 'done' },
        { id: 'cms-faq', title: 'FAQ management with reordering', status: 'done' },
      ],
    },
    {
      id: 'phase4-analytics',
      title: 'Analytics & Monitoring',
      phase: 'Phase 4: Analytics & Advanced',
      priority: 'Medium',
      features: [
        { id: 'analytics-dashboard', title: 'Dashboard with daily/monthly stats', status: 'done' },
        { id: 'analytics-users', title: 'User analytics (new, active)', status: 'done' },
        { id: 'analytics-posts', title: 'Post & listing analytics', status: 'done' },
        { id: 'analytics-reports', title: 'Most reported users', status: 'done' },
        { id: 'analytics-views', title: 'Most viewed products', status: 'done' },
        { id: 'analytics-category', title: 'Category-wise analytics', status: 'done' },
      ],
    },
    {
      id: 'phase4-marketplace',
      title: 'Marketplace Controls',
      phase: 'Phase 4: Analytics & Advanced',
      priority: 'Medium',
      features: [
        { id: 'marketplace-categories', title: 'Category enable/disable', status: 'done' },
        { id: 'marketplace-breeds', title: 'Breed category mapping enhancement', status: 'done' },
        { id: 'marketplace-block', title: 'Illegal category blocking', status: 'done' },
      ],
    },
    {
      id: 'phase4-safety',
      title: 'Safety & Legal Controls',
      phase: 'Phase 4: Analytics & Advanced',
      priority: 'Medium',
      features: [
        { id: 'safety-disclaimer', title: 'Illegal animal sale disclaimer toggle', status: 'done' },
        { id: 'safety-geo', title: 'Geo-restriction framework', status: 'done' },
        { id: 'safety-consent', title: 'User consent logs', status: 'done' },
        { id: 'safety-audit', title: 'Admin action history (audit log)', status: 'done' },
      ],
    },
    {
      id: 'phase5-smart',
      title: 'Advanced Smart Controls',
      phase: 'Phase 5: Smart Controls',
      priority: 'Low',
      features: [
        { id: 'smart-featured', title: 'Featured posts/sellers management', status: 'done' },
        { id: 'smart-expiry', title: 'Auto-expiry for marketplace listings', status: 'done' },
        { id: 'smart-price', title: 'Price range filter control', status: 'done' },
        { id: 'smart-notes', title: 'Admin notes on users (internal)', status: 'done' },
      ],
    },
  ]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'in-progress':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'done':
        return <Tag color="success">Done</Tag>;
      case 'in-progress':
        return <Tag color="processing">In Progress</Tag>;
      default:
        return <Tag>Pending</Tag>;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return '#ff4d4f';
      case 'High':
        return '#ff9800';
      case 'Medium':
        return '#1890ff';
      default:
        return '#52c41a';
    }
  };

  const calculateProgress = (category: FeatureCategory) => {
    const done = category.features.filter(f => f.status === 'done').length;
    const total = category.features.length;
    return Math.round((done / total) * 100);
  };

  const overallProgress = () => {
    const allFeatures = features.flatMap(c => c.features);
    const done = allFeatures.filter(f => f.status === 'done').length;
    const total = allFeatures.length;
    return Math.round((done / total) * 100);
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>Admin Panel Development Progress</Title>
        <Divider />
        
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Overall Progress</Text>
              <Progress 
                type="circle" 
                percent={overallProgress()} 
                size={80}
                style={{ marginTop: 16 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Total Features</Text>
              <Title level={1} style={{ margin: 0, marginTop: 8 }}>
                {features.flatMap(c => c.features).length}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Completed</Text>
              <Title level={1} style={{ margin: 0, marginTop: 8, color: '#52c41a' }}>
                {features.flatMap(c => c.features).filter(f => f.status === 'done').length}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">In Progress</Text>
              <Title level={1} style={{ margin: 0, marginTop: 8, color: '#1890ff' }}>
                {features.flatMap(c => c.features).filter(f => f.status === 'in-progress').length}
              </Title>
            </Card>
          </Col>
        </Row>

        <Collapse defaultActiveKey={['phase1-user']}>
          {features.map((category) => (
            <Panel
              key={category.id}
              header={
                <Space>
                  <span style={{ fontWeight: 'bold' }}>{category.title}</span>
                  <Tag color={getPriorityColor(category.priority)}>{category.priority}</Tag>
                  <Tag>{category.phase}</Tag>
                  <Progress 
                    percent={calculateProgress(category)} 
                    size="small" 
                    style={{ width: 150 }}
                  />
                </Space>
              }
            >
              <Row gutter={[16, 8]}>
                {category.features.map((feature) => (
                  <Col xs={24} sm={12} md={8} key={feature.id}>
                    <Card size="small" style={{ marginBottom: 8 }}>
                      <Space>
                        {getStatusIcon(feature.status)}
                        <div style={{ flex: 1 }}>
                          <Text strong>{feature.title}</Text>
                          <div style={{ marginTop: 4 }}>
                            {getStatusTag(feature.status)}
                          </div>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  );
};

export default ProgressPage;

